/* ============================================================
   BGC — bgc-core.js
   Global motion + interaction engine for the theme shell.
   Owned by AGENT-BASE. Section scripts (bgc-<name>.js) call into
   window.BGC.driveTrack / window.BGC.addToCart / window.BGC.toast
   and rely on the [data-rv] reveal system below — do not
   reimplement reveal, toast, or cart logic per-section.
   ============================================================ */

(function () {
  "use strict";

  /* motion is ALWAYS on unless ?nomotion is in the URL */
  const reduced = new URLSearchParams(location.search).has("nomotion");
  if (reduced) document.documentElement.classList.add("reduced");

  const hasGSAP = window.gsap && window.ScrollTrigger;
  if (hasGSAP) {
    gsap.registerPlugin(ScrollTrigger);
  }

  const EASE = "power4.out";

  /* ---------- scroll progress bar ---------- */
  const progressBar = document.querySelector(".scroll-progress");
  if (progressBar) {
    if (hasGSAP) {
      gsap.to(progressBar, {
        scaleX: 1,
        ease: "none",
        scrollTrigger: { trigger: document.body, start: "top top", end: "bottom bottom", scrub: 0 }
      });
    } else {
      window.addEventListener("scroll", () => {
        const pct = window.scrollY / (document.documentElement.scrollHeight - window.innerHeight);
        progressBar.style.transform = "scaleX(" + Math.min(1, pct) + ")";
      }, { passive: true });
    }
  }

  /* ---------- nav frost on scroll ---------- */
  const nav = document.querySelector(".nav");
  const onScroll = () => nav && nav.classList.toggle("scrolled", window.scrollY > 24);
  window.addEventListener("scroll", onScroll, { passive: true });
  onScroll();

  /* ---------- mobile nav toggle (hamburger dropdown) ---------- */
  const navToggle = document.querySelector("[data-nav-toggle]");
  const navPanel = document.querySelector("[data-nav-mobile-panel]");
  if (navToggle && navPanel) {
    const closeNavPanel = () => {
      navPanel.classList.remove("open");
      navToggle.setAttribute("aria-expanded", "false");
    };
    navToggle.addEventListener("click", () => {
      const open = navPanel.classList.toggle("open");
      navToggle.setAttribute("aria-expanded", open ? "true" : "false");
    });
    navPanel.querySelectorAll("a").forEach((a) => a.addEventListener("click", closeNavPanel));
    window.addEventListener("resize", () => {
      if (window.innerWidth > 820) closeNavPanel();
    });
  }

  /* ---------- pixel-snapped marquee/track driver (rAF, whole-pixel steps) ---------- */
  function driveTrack(sel, pxPerSec) {
    const track = typeof sel === "string" ? document.querySelector(sel) : sel;
    if (!track) return;
    let x = 0, paused = false, half = 0, prev = 0;
    const measure = () => { half = track.scrollWidth / 2; };
    measure();
    window.addEventListener("resize", measure);
    track.addEventListener("mouseenter", () => (paused = true));
    track.addEventListener("mouseleave", () => (paused = false));
    const tick = (now) => {
      if (!prev) prev = now;
      const dt = now - prev;
      prev = now;
      if (!paused && half) {
        x -= pxPerSec * (dt / 1000);
        if (-x >= half) x += half;
        track.style.transform = "translate3d(" + Math.round(x) + "px,0,0)";
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  /* ---------- global reveal system ([data-rv] groups of .rv) ---------- */
  if (hasGSAP && !reduced) {
    document.querySelectorAll("[data-rv]").forEach((group) => {
      const items = group.querySelectorAll(".rv");
      if (!items.length) return;
      const imgs = group.querySelectorAll(".rv img");
      gsap.fromTo(items,
        { opacity: 0, y: 44 },
        {
          opacity: 1, y: 0, duration: 1.0, ease: EASE, stagger: 0.12,
          scrollTrigger: { trigger: group, start: "top 82%" }
        });
      if (imgs.length) {
        gsap.fromTo(imgs,
          { scale: 1.06 },
          {
            scale: 1, duration: 1.1, ease: EASE, stagger: 0.12,
            scrollTrigger: { trigger: group, start: "top 82%" }
          });
      }
    });

    /* eyebrow underline draw-in */
    document.querySelectorAll("[data-rv] .eyebrow, .eyebrow.rv").forEach((eyebrow) => {
      ScrollTrigger.create({
        trigger: eyebrow, start: "top 88%", once: true,
        onEnter: () => eyebrow.classList.add("underline-in")
      });
    });
  } else {
    /* IntersectionObserver fallback — CSS-transition-driven entry reveals */
    const allRv = Array.from(document.querySelectorAll(".rv"));
    const groups = new Map();
    allRv.forEach((node) => {
      const group = node.closest("[data-rv]") || document.body;
      if (!groups.has(group)) groups.set(group, []);
      groups.get(group).push(node);
    });
    const ioReveal = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        ioReveal.unobserve(entry.target);
        const group = entry.target;
        const nodes = groups.get(group) || [group];
        nodes.forEach((node, i) => {
          node.style.transitionDelay = (i * 110) + "ms";
          node.classList.add("rv-in");
        });
      });
    }, { threshold: 0.15 });
    groups.forEach((nodes, group) => {
      if (group === document.body) {
        nodes.forEach((node) => {
          const singleIO = new IntersectionObserver((entries) => {
            entries.forEach((e) => {
              if (!e.isIntersecting) return;
              singleIO.unobserve(e.target);
              e.target.style.transitionDelay = "0ms";
              e.target.classList.add("rv-in");
            });
          }, { threshold: 0.15 });
          singleIO.observe(node);
        });
      } else {
        ioReveal.observe(group);
      }
    });

    /* eyebrow underline draw-in fallback */
    document.querySelectorAll("[data-rv] .eyebrow, .eyebrow.rv").forEach((eyebrow) => {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          io.unobserve(e.target);
          e.target.classList.add("underline-in");
        });
      }, { threshold: 0.4 });
      io.observe(eyebrow);
    });
  }

  window.addEventListener("load", () => {
    if (hasGSAP) ScrollTrigger.refresh();
  });

  /* Safety net: after 2.5s any in-viewport .rv still at opacity 0 gets revealed */
  setTimeout(() => {
    document.querySelectorAll(".rv").forEach((node) => {
      const rect = node.getBoundingClientRect();
      const inViewport = rect.top < window.innerHeight && rect.bottom > 0;
      if (inViewport) {
        const computed = window.getComputedStyle(node).opacity;
        if (parseFloat(computed) < 0.1) {
          node.style.transitionDelay = "0ms";
          node.classList.add("rv-in");
        }
      }
    });
    document.querySelectorAll(".eyebrow").forEach((eyebrow) => {
      const rect = eyebrow.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) eyebrow.classList.add("underline-in");
    });
  }, 2500);

  /* ---------- toast helper ---------- */
  const toastEl = document.querySelector(".toast");
  let toastTimer;
  function showToast(message) {
    if (!toastEl) return;
    const label = toastEl.querySelector("span:last-child");
    if (label) label.textContent = message || "Added to cart";
    toastEl.classList.add("show");
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove("show"), 2600);
  }

  /* ---------- cart bubble ---------- */
  const cartCountEls = () => document.querySelectorAll("[data-bgc-cart-count]");
  function updateCartCount(count) {
    cartCountEls().forEach((el) => {
      el.textContent = count;
      el.classList.toggle("show", count > 0);
      if (hasGSAP && !reduced) {
        gsap.fromTo(el, { scale: 1.6 }, { scale: 1, duration: 0.5, ease: "back.out(2)" });
      }
    });
  }

  function refreshCart() {
    return fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then((r) => r.json())
      .then((cart) => {
        updateCartCount(cart.item_count);
        return cart;
      })
      .catch(() => {});
  }

  /* ---------- window.BGC ---------- */
  let addQueue = Promise.resolve();
  function addToCart(variantId, qty) {
    qty = qty || 1;
    addQueue = addQueue.then(() =>
      fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ id: variantId, quantity: qty })
      })
        .then((r) => r.json())
        .then((item) => {
          return refreshCart().then(() => {
            const title = (item && (item.product_title || item.title)) || "Item";
            showToast(title + " added to cart");
            return item;
          });
        })
        .catch(() => {
          showToast("Couldn't add to cart — try again");
        })
    );
    return addQueue;
  }

  window.BGC = window.BGC || {};
  window.BGC.addToCart = addToCart;
  window.BGC.toast = showToast;
  window.BGC.driveTrack = driveTrack;
  window.BGC.refreshCart = refreshCart;
})();
